/**
 * Soft Pay now offer rules (London Smoke & Cure).
 * AND within a rule row; OR across enabled rows. Pure — used by cart + mirrored on Cloud Run.
 *
 * SKU exclusions: global list + per-rule excludedSkus block Pay now when the cart/order
 * contains a weigh-on-ship (or otherwise excluded) product code.
 */

export const PAY_NOW_CHANNELS = ['web_checkout', 'admin_on_behalf', 'standing'];

export const PAY_NOW_ACCOUNT_AGES = ['any', 'first_order', 'new_trade', 'existing_trade'];

/** Account boolean flags operators can require on a rule. */
export const PAY_NOW_ACCOUNT_FLAG_KEYS = ['mustPay', 'noCredit'];

export const PAY_NOW_OVERRIDE = {
  on: 'on',
  off: 'off',
  inherit: 'inherit',
};

function clean(v) {
  return v == null ? '' : String(v).trim();
}

function num(v, fallback = null) {
  if (v == null || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function asStringList(v) {
  if (!Array.isArray(v)) return [];
  return v.map(clean).filter(Boolean);
}

/** Normalize SKU / product codes for case-insensitive compare. */
export function normalizeSkuList(list) {
  const seen = new Set();
  const out = [];
  for (const raw of asStringList(list)) {
    const key = raw.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(raw);
  }
  return out;
}

function skuKey(v) {
  return clean(v).toUpperCase();
}

/**
 * Collect product codes from an order and/or cart lines.
 */
export function collectOrderSkus(order = {}, lineItems = []) {
  const lines = Array.isArray(lineItems) && lineItems.length
    ? lineItems
    : order.order_items || order.items || order.orderItems || [];
  const out = new Set();
  for (const line of lines || []) {
    if (!line || typeof line !== 'object') continue;
    for (const key of ['code', 'sku', 'SKU', 'productCode', 'itemCode', 'id', 'productId']) {
      const v = clean(line[key]);
      if (v) out.add(skuKey(v));
    }
  }
  return [...out];
}

function findExcludedHit(orderSkus, excludedList) {
  const excluded = new Set(normalizeSkuList(excludedList).map(skuKey));
  if (!excluded.size || !orderSkus.length) return null;
  for (const sku of orderSkus) {
    if (excluded.has(sku)) return sku;
  }
  return null;
}

/** Days since account created — new_trade threshold. */
export const NEW_TRADE_MAX_AGE_DAYS = 90;

/**
 * Normalize one rule row from Firestore / editor.
 */
export function normalizePayNowRule(raw = {}, index = 0) {
  const id = clean(raw.id) || `rule_${index + 1}`;
  const accountAge = clean(raw.accountAge) || 'any';
  return {
    id,
    enabled: raw.enabled !== false,
    label: clean(raw.label) || '',
    channels: asStringList(raw.channels),
    shippingMethods: asStringList(raw.shippingMethods),
    accountFlags: asStringList(raw.accountFlags).filter((f) =>
      PAY_NOW_ACCOUNT_FLAG_KEYS.includes(f)
    ),
    accountAge: PAY_NOW_ACCOUNT_AGES.includes(accountAge) ? accountAge : 'any',
    minTotal: num(raw.minTotal, null),
    maxTotal: num(raw.maxTotal, null),
    /** Product codes that block this rule when present on the order. */
    excludedSkus: normalizeSkuList(raw.excludedSkus),
  };
}

export function normalizePayNowRules(list) {
  if (!Array.isArray(list)) return [];
  return list.map((row, i) => normalizePayNowRule(row, i));
}

export function createEmptyPayNowRule() {
  return normalizePayNowRule({
    id: `rule_${Date.now().toString(36)}`,
    enabled: true,
    label: '',
    channels: ['web_checkout'],
    shippingMethods: [],
    accountFlags: [],
    accountAge: 'any',
    minTotal: null,
    maxTotal: null,
    excludedSkus: [],
  });
}

/**
 * Default soft-offer rules for accounts that must pay at checkout
 * (mustPay or noCredit). Used when flipping live flags for “those paying”.
 */
export function buildDefaultPayingAccountPayNowRules() {
  return normalizePayNowRules([
    {
      id: 'web_must_pay',
      enabled: true,
      label: 'Web checkout — Must pay',
      channels: ['web_checkout'],
      shippingMethods: [],
      accountFlags: ['mustPay'],
      accountAge: 'any',
      minTotal: null,
      maxTotal: null,
      excludedSkus: [],
    },
    {
      id: 'web_no_credit',
      enabled: true,
      label: 'Web checkout — No credit',
      channels: ['web_checkout'],
      shippingMethods: [],
      accountFlags: ['noCredit'],
      accountAge: 'any',
      minTotal: null,
      maxTotal: null,
      excludedSkus: [],
    },
  ]);
}

function resolveOverride(account = {}) {
  const raw = clean(account.payNowOverride || account.pay_now_override).toLowerCase();
  if (raw === 'on' || raw === 'force_on' || raw === 'yes') return PAY_NOW_OVERRIDE.on;
  if (raw === 'off' || raw === 'force_off' || raw === 'no') return PAY_NOW_OVERRIDE.off;
  return PAY_NOW_OVERRIDE.inherit;
}

function accountHasFlag(account = {}, flagKey) {
  if (flagKey === 'mustPay') {
    return account.mustPay === true || account.must_pay === true;
  }
  if (flagKey === 'noCredit') {
    return account.noCredit === true || account.no_credit === true;
  }
  return account[flagKey] === true;
}

function isNewTradeAccount(account = {}, now = new Date()) {
  const createdRaw =
    account.createdAt ||
    account.created_at ||
    account.dateCreated ||
    account.accountCreatedAt ||
    null;
  let created = null;
  if (createdRaw && typeof createdRaw.toDate === 'function') {
    created = createdRaw.toDate();
  } else if (createdRaw) {
    created = new Date(createdRaw);
  }
  if (!created || Number.isNaN(created.getTime())) {
    const orderCount = num(account.orderCount ?? account.priorOrderCount, null);
    if (orderCount != null) return orderCount <= 3;
    return false;
  }
  const days = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
  return days <= NEW_TRADE_MAX_AGE_DAYS;
}

function matchAccountAge(ruleAge, account, ctx) {
  if (!ruleAge || ruleAge === 'any') return true;
  const prior = num(ctx.priorOrderCount ?? account.priorOrderCount ?? account.orderCount, 0);
  if (ruleAge === 'first_order') return prior <= 0;
  if (ruleAge === 'new_trade') return isNewTradeAccount(account, ctx.now);
  if (ruleAge === 'existing_trade') return !isNewTradeAccount(account, ctx.now);
  return true;
}

function matchRuleRow(rule, ctx) {
  if (!rule || rule.enabled === false) return false;

  if (rule.channels.length && !rule.channels.includes(ctx.channel)) return false;

  if (rule.shippingMethods.length) {
    const ship = clean(ctx.shippingMethod);
    const hit = rule.shippingMethods.some(
      (m) => m.toLowerCase() === ship.toLowerCase()
    );
    if (!hit) return false;
  }

  for (const flag of rule.accountFlags) {
    if (!accountHasFlag(ctx.account, flag)) return false;
  }

  if (!matchAccountAge(rule.accountAge, ctx.account, ctx)) {
    return false;
  }

  const total = num(ctx.orderTotal, null);
  if (rule.minTotal != null && (total == null || total < rule.minTotal)) return false;
  if (rule.maxTotal != null && (total == null || total > rule.maxTotal)) return false;

  if (findExcludedHit(ctx.orderSkus || [], rule.excludedSkus)) {
    return false;
  }

  return true;
}

/**
 * @param {object} opts
 * @param {boolean} opts.enabled - master switch payNowOffersEnabled
 * @param {object[]} opts.rules
 * @param {string[]} [opts.excludedSkus] - global weigh-on-ship (etc.) SKU blocklist
 * @param {object} [opts.account] - users/{uid} fields + payNowOverride
 * @param {object} [opts.order] - order snapshot (shipping_method, order_total, order_items)
 * @param {object[]} [opts.lineItems] - cart lines when order items not yet on order
 * @param {string} opts.channel - web_checkout | admin_on_behalf | standing
 * @param {number} [opts.priorOrderCount] - orders before this one (0 = first)
 * @param {Date} [opts.now]
 * @returns {{ offer: boolean, reason: string, matchedRuleId?: string|null, excludedSku?: string|null }}
 */
export function shouldOfferPayNow({
  enabled = false,
  rules = [],
  excludedSkus = [],
  account = {},
  order = {},
  lineItems = [],
  channel = 'web_checkout',
  priorOrderCount,
  now = new Date(),
} = {}) {
  if (!enabled) {
    return { offer: false, reason: 'master_off', matchedRuleId: null, excludedSku: null };
  }

  const orderSkus = collectOrderSkus(order, lineItems);
  const globalHit = findExcludedHit(orderSkus, excludedSkus);
  if (globalHit) {
    return {
      offer: false,
      reason: 'excluded_sku',
      matchedRuleId: null,
      excludedSku: globalHit,
    };
  }

  const override = resolveOverride(account);
  if (override === PAY_NOW_OVERRIDE.off) {
    return { offer: false, reason: 'account_override_off', matchedRuleId: null, excludedSku: null };
  }
  if (override === PAY_NOW_OVERRIDE.on) {
    return { offer: true, reason: 'account_override_on', matchedRuleId: null, excludedSku: null };
  }

  const normalized = normalizePayNowRules(rules);
  const enabledRules = normalized.filter((r) => r.enabled);
  if (!enabledRules.length) {
    return { offer: false, reason: 'no_enabled_rules', matchedRuleId: null, excludedSku: null };
  }

  const shippingMethod =
    clean(order.shipping_method) ||
    clean(order.shippingMethod) ||
    clean((order.checkout_data || order.checkoutData || {}).shipping_method);

  const orderTotal = num(
    order.order_total ?? order.orderTotal ?? order.total ?? order.finalTotal,
    null
  );

  const ctx = {
    channel: clean(channel) || 'web_checkout',
    shippingMethod,
    orderTotal,
    account,
    orderSkus,
    priorOrderCount:
      priorOrderCount != null
        ? num(priorOrderCount, 0)
        : num(account.priorOrderCount, 0),
    now,
  };

  for (const rule of enabledRules) {
    if (matchRuleRow(rule, ctx)) {
      return {
        offer: true,
        reason: 'rule_match',
        matchedRuleId: rule.id,
        excludedSku: null,
      };
    }
  }

  // If every candidate rule failed only because of per-rule SKU exclusion, surface that.
  const anyRuleSkuBlock = enabledRules.some((rule) => {
    if (!rule.channels.length || rule.channels.includes(ctx.channel)) {
      return !!findExcludedHit(orderSkus, rule.excludedSkus);
    }
    return false;
  });
  if (anyRuleSkuBlock && orderSkus.length) {
    const hit =
      enabledRules.map((r) => findExcludedHit(orderSkus, r.excludedSkus)).find(Boolean) || null;
    return {
      offer: false,
      reason: 'excluded_sku',
      matchedRuleId: null,
      excludedSku: hit,
    };
  }

  return { offer: false, reason: 'no_rule_match', matchedRuleId: null, excludedSku: null };
}
